/**
 * 📞 JONLI QO'NG'IROQ DVIGATELI (Boshqaruvchi F6, 2026-09-27) — docs/knowledge/152.
 * Tugmasiz uzluksiz suhbat: mikrofon doim ochiq, gap boshlanishi/tugashi ovoz balandligidan (VAD)
 * aniqlanadi, gap tugagach WAV (16 kHz mono) `onUtterance` ga beriladi; agent javobi (mp3 bo'laklar)
 * shu AudioContext orqali navbat bilan o'ynaydi. Agent gapirayotganda foydalanuvchi gapirsa —
 * `onBargeIn` (ijro to'xtaydi, server qolgan ovozni tayyorlamaydi).
 *
 * Nega ScriptProcessor (AudioWorklet emas): worklet alohida modul fayl/blob URL talab qiladi (CSP),
 * Safari/iOS da ham ishonchli emas edi; 2048 namuna ≈ 43 ms kadr — VAD uchun yetarli.
 * Nega MediaRecorder emas: gap BOSHLANISHI ovoz sezilgandan keyin ma'lum bo'ladi — MediaRecorder'ga
 * oldingi ~0.4 s ni qo'shib bo'lmaydi (so'z boshi kesilardi). Xom PCM + oldingi bufer (pre-roll).
 *
 * Aks-sado: getUserMedia echoCancellation yoqilgan. ✋ Bo'lish (2026-10-01 qayta yozildi): ilgari agent gapirayotganda
 * chegara qat'iy `max(floor×6, 0.05)` + 350 ms edi — oddiy ovoz noutbuk mikrofonida bunga yetmasdi (7 kunda 9 ovozli
 * navbatdan 1 ta bo'lish). Endi: (1) chiqish (dinamik) darajasi AnalyserNode bilan o'lchanadi va mikrofonga qaytgan
 * aks-sado NISBATI o'rganiladi (`echoRatio`) — chegara = shu nisbatdagi kutilgan aks-sadodan 2.5× baland;
 * (2) ~180 ms dan keyin agent ovozi pasayadi («duck»), gap 420 ms davom etsa to'xtaydi, aks holda ovoz qaytadi
 * (yo'tal/«hm» gapni kesmaydi); (3) `onSpeechStart` — agent o'ylayotganda gap boshlansa panel navbatni bekor qiladi.
 * `stats()` — o'lchovlar (qo'ng'iroq oxirida serverga, chegarani haqiqiy ma'lumot bilan sozlash uchun). Quloqchin bilan eng yaxshi.
 */
import React from 'react';

const FRAME = 2048;

function encodeWav(chunks, inRate, outRate = 16000) {
  let len = 0; for (const c of chunks) len += c.length;
  const all = new Float32Array(len);
  let o = 0; for (const c of chunks) { all.set(c, o); o += c.length; }
  const ratio = inRate / outRate;
  const n = Math.floor(len / ratio);
  const pcm = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    const a = Math.floor(i * ratio), b = Math.min(len, Math.floor((i + 1) * ratio));
    let s = 0; for (let j = a; j < b; j++) s += all[j];
    const v = Math.max(-1, Math.min(1, b > a ? s / (b - a) : all[a] || 0));
    pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  const buf = new ArrayBuffer(44 + pcm.length * 2);
  const dv = new DataView(buf);
  const w = (off, str) => { for (let i = 0; i < str.length; i++) dv.setUint8(off + i, str.charCodeAt(i)); };
  w(0, 'RIFF'); dv.setUint32(4, 36 + pcm.length * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, outRate, true); dv.setUint32(28, outRate * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
  w(36, 'data'); dv.setUint32(40, pcm.length * 2, true);
  new Int16Array(buf, 44).set(pcm);
  return new Blob([buf], { type: 'audio/wav' });
}

/**
 * getUserMedia xatosi → aniq sabab. Ilgari hammasi «brauzer sozlamasidan ruxsat bering» edi — 2026-09-27 da ruxsat
 * yoqilgan bo'lsa ham shu chiqdi (asl sabab sayt sarlavhasi `Permissions-Policy: microphone=()` edi).
 */
export function micErrorText(e) {
  const name = String((e && e.name) || ''), msg = String((e && e.message) || e || '');
  if (/policy/i.test(msg)) return 'Sayt siyosati mikrofonni taqiqlagan (Permissions-Policy) — administratorga ayting';
  if (/system/i.test(msg)) return "Kompyuter mikrofonni brauzerga bermagan: Tizim sozlamalari → Maxfiylik → Mikrofon → brauzerni yoqing, keyin brauzerni qayta oching";
  if (/NotAllowed|Permission/i.test(name + msg)) return "Mikrofonga ruxsat yo'q: manzil satridagi 🔒 → Mikrofon → Ruxsat, keyin sahifani yangilang";
  if (/NotFound|DevicesNotFound/i.test(name)) return 'Mikrofon topilmadi — ulangan mikrofonni tekshiring';
  if (/NotReadable|TrackStart/i.test(name)) return 'Mikrofon band — boshqa dastur (Zoom, Meet…) ishlatayotgan bo\'lishi mumkin';
  if (!window.isSecureContext) return 'Mikrofon faqat https sahifada ishlaydi';
  return `Mikrofon ochilmadi: ${msg || name}`;
}

export function blobToBase64(blob) {
  return new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1] || ''); fr.onerror = rej; fr.readAsDataURL(blob); });
}

export class LiveCallEngine {
  constructor({ onState, onLevel, onUtterance, onBargeIn, onSpeechStart, onError, endSilenceMs = 0, allowBargeIn = true } = {}) {
    Object.assign(this, { onState, onLevel, onUtterance, onBargeIn, onSpeechStart, onError, endSilenceMs, allowBargeIn });
    this.echoRatio = 0.3;     // mikrofon RMS / dinamik RMS (aks-sado) — o'rganiladi (~1 s da); 0.3 = ehtiyotkor boshlanish
    this.outHist = [];        // oxirgi ~260 ms dinamik darajasi (aks-sado kechikib keladi)
    this.ducked = false;
    this.st = { t0: Date.now(), bargeIns: 0, falseDucks: 0, speechStarts: 0, busyStarts: 0, playMs: 0, maxOut: 0, maxMicPlaying: 0,
      bargeLevels: [], falseLevels: [], ratioSamples: [] };
    this.state = 'idle';
    this.muted = false;
    this.floor = 0.008;
    this.frames = 0;
    this.sources = [];
    this.head = 0;
    this.chain = Promise.resolve();
    this._reset();
  }
  _reset() { this.inSpeech = false; this.above = 0; this.silentMs = 0; this.speechMs = 0; this.buf = []; this.pre = []; }
  _set(st) { if (this.state !== st) { this.state = st; try { this.onState && this.onState(st); } catch (e) { } } }
  get playing() { return this.sources.length > 0; }

  /** Tugma bosilganda chaqiring (iOS AudioContext'ni faqat foydalanuvchi harakatida ochadi). */
  async start() {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || !navigator.mediaDevices) throw new Error('Bu brauzer jonli ovozni qo\'llamaydi');
    this.ctx = new AC();
    if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => { });
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
    this.src = this.ctx.createMediaStreamSource(this.stream);
    this.proc = this.ctx.createScriptProcessor(FRAME, 1, 1);
    this.frameMs = FRAME / this.ctx.sampleRate * 1000;
    this.preMax = Math.ceil(600 / this.frameMs);   // bo'lish 420 ms da tasdiqlanadi — so'z boshi kesilmasin
    this.src.connect(this.proc);
    // Chiqish nol (mikrofon dinamikka qaytmaydi) — ba'zi brauzerlarda ulanmasa onaudioprocess ishlamaydi.
    this.sink = this.ctx.createGain(); this.sink.gain.value = 0;
    this.proc.connect(this.sink); this.sink.connect(this.ctx.destination);
    this.out = this.ctx.createGain(); this.out.connect(this.ctx.destination);
    // Dinamikka ketayotgan ovoz darajasi — aks-sadoni foydalanuvchi ovozidan ajratish uchun.
    this.an = this.ctx.createAnalyser(); this.an.fftSize = 1024; this.out.connect(this.an);
    this.anBuf = new Float32Array(this.an.fftSize);
    this.proc.onaudioprocess = (e) => { try { this._frame(e.inputBuffer.getChannelData(0)); } catch (err) { } };
    this._set('listening');
  }

  stop() {
    this.stopPlayback();
    try { this.proc && (this.proc.onaudioprocess = null); } catch (e) { }
    try { this.src && this.src.disconnect(); this.proc && this.proc.disconnect(); } catch (e) { }
    try { this.stream && this.stream.getTracks().forEach(t => t.stop()); } catch (e) { }
    try { this.ctx && this.ctx.close(); } catch (e) { }
    this.ctx = null; this._reset(); this._set('idle');
  }

  setMuted(m) { this.muted = !!m; if (m && this.inSpeech) this._reset(); }
  setBusy(b) { this.busy = !!b; if (!this.inSpeech && !this.playing) this._set(b ? 'thinking' : 'listening'); }

  _frame(x) {
    let s = 0; for (let i = 0; i < x.length; i++) s += x[i] * x[i];
    const rms = Math.sqrt(s / x.length);
    this.frames++;
    this.level = this.muted ? 0 : rms;   // CallBar o'zi o'qiydi (butun suhbat qayta chizilmasin)
    if (this.onLevel && this.frames % 2 === 0) { try { this.onLevel(this.level); } catch (e) { } }
    if (this.muted) return;
    const copy = new Float32Array(x);
    if (!this.inSpeech) {
      this.pre.push(copy); if (this.pre.length > this.preMax) this.pre.shift();
      // Shovqin darajasi — jim paytlarda sekin moslashadi (boshida tezroq).
      const k = this.frames < 25 ? 0.2 : 0.03;
      // Agent gapirayotganda o'rganilmaydi — aks-sado shovqin darajasini ko'tarib, keyingi tinglashni kar qilardi.
      if (!this.playing && (rms < this.floor * 2.5 || this.frames < 25)) this.floor = Math.max(0.002, this.floor * (1 - k) + rms * k);
      const playing = this.playing;
      if (playing && !this.allowBargeIn) { this.above = 0; return; }
      if (!playing) {
        if (this.ducked) this._unduck();
        this.above = rms > Math.max(this.floor * 3, 0.012) ? this.above + 1 : 0;
        if (this.above * this.frameMs >= 120) this._begin(false, rms);
        return;
      }
      // Agent gapirayapti: kutilgan aks-sado = dinamik darajasi × o'rganilgan nisbat.
      const out = this._outLevel();
      this.st.playMs += this.frameMs;
      if (out > this.st.maxOut) this.st.maxOut = out;
      if (rms > this.st.maxMicPlaying) this.st.maxMicPlaying = rms;
      const echo = out * this.echoRatio;
      const thr = Math.max(this.floor * 4, 0.012, echo * 2.5);
      if (rms > thr) {
        this.above++;
        const ms = this.above * this.frameMs;
        if (ms >= 180 && !this.ducked) this._duck();
        if (ms >= 420) { this.st.bargeIns++; this._keep(this.st.bargeLevels, [+rms.toFixed(4), +out.toFixed(4), +this.echoRatio.toFixed(3)]); this._begin(true, rms); }
        return;
      }
      if (this.above > 0 && this.ducked) {   // qisqa tovush (yo'tal, «hm») — gap emas, ovoz qaytadi
        this.st.falseDucks++; this._keep(this.st.falseLevels, [+rms.toFixed(4), +out.toFixed(4), +this.echoRatio.toFixed(3)]);
        this._unduck();
      }
      this.above = 0;
      // Nisbatni o'rganish: foydalanuvchi jim, dinamik yetarli baland — mikrofonga kelgan hamma narsa aks-sado.
      // Isinish (qo'ng'iroqdagi birinchi ~1.5 s ijro) — tez o'rganadi; keyin «noaniq zona» (kutilgandan 1.3× baland —
      // past ovozli gap bo'lishi mumkin) SEKIN o'rganiladi, aks holda gapirgan odamning ovozi «aks-sado» bo'lib qolib,
      // keyin umuman bo'lib bo'lmasdi (simulyatsiyada nisbat 0.3→0.46 ga chiqib ketgan).
      if (out > 0.01) {
        const r = Math.min(2, rms / out);
        const warm = this.st.playMs < 1500;
        const k = warm ? 0.12 : r <= this.echoRatio * 1.3 ? (r > this.echoRatio ? 0.06 : 0.04) : 0.005;
        this.echoRatio = Math.max(0.01, this.echoRatio * (1 - k) + r * k);
        if (this.frames % 40 === 0) this._keep(this.st.ratioSamples, +this.echoRatio.toFixed(3));
      }
      return;
    }
    this.buf.push(copy);
    const endThr = Math.max(this.floor * 2, 0.008);
    if (rms < endThr) this.silentMs += this.frameMs; else { this.silentMs = 0; this.speechMs += this.frameMs; }
    if (!this.sustained && this.speechMs >= 450) this._sustain(false, rms);
    const total = this.buf.length * this.frameMs;
    // ⚡ Gap tugashi (2026-10-01): qat'iy 700 ms o'rniga moslashuvchan — qisqa gap (<1 s: «ha», «yo'q») 450 ms
    // jimlikda yuboriladi, uzun gap (odam o'ylab to'xtaydi) 650 ms. endSilenceMs berilsa — qat'iy shu qiymat.
    const endMs = this.endSilenceMs || (this.speechMs < 1000 ? 450 : 650);
    if (this.silentMs >= endMs || total >= 30000) {
      const buf = this.buf, speechMs = this.speechMs, rate = this.ctx.sampleRate;
      this._reset();
      this._set(this.busy ? 'thinking' : 'listening');
      if (speechMs >= 250) {
        // Oxirgi jimlikning ko'p qismini kesamiz (server ortiqcha ovoz tinglamasin), 200 ms qoladi.
        const drop = Math.max(0, Math.floor((endMs - 200) / this.frameMs));
        const keep = buf.slice(0, Math.max(1, buf.length - drop));
        try { this.onUtterance && this.onUtterance(encodeWav(keep, rate), Math.round(speechMs)); } catch (e) { }
      }
    }
  }

  _begin(barge, rms) {
    this.inSpeech = true; this.buf = [...this.pre]; this.pre = []; this.silentMs = 0; this.speechMs = this.above * this.frameMs; this.above = 0;
    this.st.speechStarts++;
    if (barge) { this.stopPlayback(); this._unduck(); try { this.onBargeIn && this.onBargeIn(); } catch (e) { } }
    // onSpeechStart — gap ~450 ms davom etgandagina (yo'tal/eshik tovushi agent tayyorlayotgan javobni bekor qilmasin).
    this.sustained = false;
    if (barge) this._sustain(true, rms);
    this._set('speech');
  }
  _sustain(barge, rms) {
    this.sustained = true;
    if (this.busy) this.st.busyStarts++;
    try { this.onSpeechStart && this.onSpeechStart({ barge, busy: !!this.busy, rms }); } catch (e) { }
  }
  _outLevel() {
    let o = 0;
    if (this.an) {
      this.an.getFloatTimeDomainData(this.anBuf);
      let s = 0; for (let i = 0; i < this.anBuf.length; i++) s += this.anBuf[i] * this.anBuf[i];
      o = Math.sqrt(s / this.anBuf.length);   // pasaytirilgandan KEYINGI daraja — mikrofonga aynan shu qaytadi
    }
    this.outHist.push(o); if (this.outHist.length > 6) this.outHist.shift();
    return Math.max(...this.outHist);
  }
  _duck() { this.ducked = true; this.outHist = []; /* chegara endi pasaygan ovozga qarab */ try { this.out.gain.setTargetAtTime(0.18, this.ctx.currentTime, 0.03); } catch (e) { this.out.gain.value = 0.18; } }
  _unduck() {
    if (!this.ducked) return; this.ducked = false;
    try { this.out.gain.cancelScheduledValues(this.ctx.currentTime); this.out.gain.setTargetAtTime(1, this.ctx.currentTime, 0.05); } catch (e) { if (this.out) this.out.gain.value = 1; }
  }
  _keep(arr, v) { arr.push(v); if (arr.length > 40) arr.shift(); }
  /** Qo'ng'iroq o'lchovlari (serverga yuboriladi). */
  stats() {
    return { ...this.st, durMs: Date.now() - this.st.t0, echoRatio: +this.echoRatio.toFixed(3), floor: +this.floor.toFixed(4),
      ua: (navigator.userAgent || '').slice(0, 160) };
  }

  /** mp3 (base64) bo'lagini navbatga qo'yish. */
  playMp3(b64) {
    if (!this.ctx) return;
    this.chain = this.chain.then(async () => {
      if (!this.ctx) return;
      try {
        const bin = atob(b64); const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        const audio = await new Promise((res, rej) => this.ctx.decodeAudioData(u8.buffer, res, rej));
        if (!this.ctx) return;
        const src = this.ctx.createBufferSource(); src.buffer = audio; src.connect(this.out);
        const start = Math.max(this.ctx.currentTime + 0.03, this.head);
        src.start(start); this.head = start + audio.duration; this.sources.push(src);
        if (!this.inSpeech) this._set('speaking');
        src.onended = () => {
          this.sources = this.sources.filter(x => x !== src);
          if (!this.sources.length && !this.inSpeech) this._set(this.busy ? 'thinking' : 'listening');
        };
      } catch (e) { /* buzuq bo'lak — keyingisi */ }
    });
  }

  stopPlayback() {
    this.sources.forEach(s => { try { s.onended = null; s.stop(); } catch (e) { } });
    this.sources = []; this.head = 0; this.chain = Promise.resolve(); this.outHist = [];
    if (this.ducked) this._unduck();
  }
}

export const CALL_STATE_LABEL = {
  idle: 'Ulanmoqda…',
  listening: 'Tinglayapman — gapiring',
  speech: 'Eshityapman…',
  thinking: 'O\'ylayapman…',
  speaking: 'Gapiryapman — gapirsangiz to\'xtayman',
};

const fmtSecs = (n) => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;

/** Qo'ng'iroq paneli: holat doirasi (ovoz balandligi bilan), vaqt, 🎙 / ✋ / 📴. */
export function CallBar({ state, engine, secs = 0, muted, onMute, onInterrupt, onHangup, note, cost }) {
  const [level, setLevel] = React.useState(0);
  React.useEffect(() => {
    if (!engine) return undefined;
    const t = setInterval(() => setLevel(engine.level || 0), 80);
    return () => clearInterval(t);
  }, [engine]);
  const ring = {
    listening: 'bg-emerald-500', speech: 'bg-emerald-600', thinking: 'bg-amber-500', speaking: 'bg-primary-600', idle: 'bg-gray-400',
  }[state] || 'bg-gray-400';
  const scale = state === 'speech' ? 1 + Math.min(0.35, level * 4) : 1;
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
        {(state === 'listening' || state === 'speaking') && <span className={`absolute inset-0 rounded-full ${ring} opacity-30 animate-ping`} />}
        <span className={`relative w-12 h-12 rounded-full ${muted ? 'bg-gray-400' : ring} text-white text-xl flex items-center justify-center transition-transform duration-75`}
          style={{ transform: `scale(${scale})` }}>
          {muted ? '🔇' : state === 'thinking' ? '…' : state === 'speaking' ? '🔊' : '🎙'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{muted ? 'Mikrofon o\'chiq' : (CALL_STATE_LABEL[state] || state)}</div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
          📞 {fmtSecs(secs)}{cost != null ? ` · $${Number(cost).toFixed(3)}` : ''}{note ? ` · ${note}` : ''}
        </div>
      </div>
      <button onClick={onMute} title={muted ? 'Mikrofonni yoqish' : 'Mikrofonni o\'chirish'}
        className={`w-9 h-9 rounded-full border text-sm ${muted ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-300' : 'border-gray-300 dark:border-gray-600'}`}>{muted ? '🎙' : '🔇'}</button>
      {state === 'speaking' && onInterrupt && (
        <button onClick={onInterrupt} title="Gapini to'xtatish" className="w-9 h-9 rounded-full border border-gray-300 dark:border-gray-600 text-sm">✋</button>
      )}
      <button onClick={onHangup} title="Qo'ng'iroqni tugatish" className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 text-white text-lg">📴</button>
    </div>
  );
}
